// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React, { useEffect } from 'react';
import { Spin, Row, Col, Alert } from 'antd';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLogin } from '@/features/auth/useLogin';
import sdtcLogo from '@/assets/sdtc.svg';
import loginIllustration from '@/assets/login.svg';
import mailIcon from '@/assets/mail.svg';
import passIcon from '@/assets/pass.svg';
import styles from './LoginPage.module.css';
import { AUTH_MODE } from '@/config/constants';
import { shouldRedirectToSdpmSso } from '@/utils/authRouting';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();

  useEffect(() => {
    const hostname = window.location.hostname.toLowerCase();
    if (shouldRedirectToSdpmSso(hostname, AUTH_MODE)) {
      window.location.replace('https://sdpm.vn/login');
    }
  }, []);
  const {
    email,
    password,
    showPassword,
    setShowPassword,
    emailError,
    passwordError,
    loading,
    error,
    handleEmailChange,
    handlePasswordChange,
    handleSubmit,
    handleForgotPassword,
  } = useLogin();
  // Điều hướng tự động nếu đã đăng nhập
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (user?.role === 'staff') {
        navigate('/personal/overview', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  useEffect(() => {
    document.body.classList.add('loginPage');
    return () => {
      document.body.classList.remove('loginPage');
    };
  }, []);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Row className={styles.container}>
      <Col xs={0} md={14} className={styles.leftColumn}>
        <div className={styles.textContainer}>
          <h1 className={styles.mainTitle}>
            Hệ thống Quản lý Nhân sự Toàn diện
          </h1>
          <p className={styles.subtitle}>
            Nâng cao hiệu quả làm việc, tối ưu quy trình và kết nối con người trong tổ chức của bạn.
          </p>
        </div>
        <div className={styles.imageContainer}>
          <img src={loginIllustration} alt="Login Illustration" className={styles.illustration} />
        </div>
      </Col>

      <Col xs={24} md={10} className={styles.rightColumn}>
        <div className={styles.formContainer}>
          <div className={styles.logoContainer}>
            <img src={sdtcLogo} alt="SDTC Logo" />
          </div>
          <h2 className={styles.formTitle}>
            Đăng nhập vào tài khoản của bạn
          </h2>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              style={{ marginBottom: 16 }}
            />
          )}

          <form onSubmit={handleSubmit} className={styles.loginForm}>
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.formLabel}>Email</label>
              <div className={styles.inputWrapper}>
                <img src={mailIcon} alt="Email" className={styles.inputIcon} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Nhập email của bạn"
                  autoComplete="email"
                  className={`${styles.customInput} ${emailError ? styles.inputError : ''}`}
                />
              </div>
              {emailError && <span className={styles.errorMessage}>{emailError}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.formLabel}>Mật khẩu</label>
              <div className={styles.inputWrapper}>
                <img src={passIcon} alt="Password" className={styles.inputIcon} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Nhập mật khẩu"
                  autoComplete="current-password"
                  className={`${styles.customInput} ${styles.passwordInput} ${passwordError ? styles.inputError : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.eyeButton}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                >
                  {showPassword ? (
                    <Eye size={20} color="#999" />
                  ) : (
                    <EyeOff size={20} color="#999" />
                  )}
                </button>
              </div>
              {passwordError && <span className={styles.errorMessage}>{passwordError}</span>}
            </div>

            <div className={styles.forgotPasswordContainer}>
              <button type="button" onClick={handleForgotPassword} className={styles.forgotPasswordButton}>
                Quên mật khẩu?
              </button>
            </div>

            <button type="submit" disabled={loading} className={styles.submitButton}>
              {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
            </button>

            <p className={styles.privacyPolicyRow}>
              Trước khi sử dụng, bạn hãy xem
              <button
                type="button"
                className={styles.privacyPolicyLink}
                onClick={() => navigate('/privacy-policy')}
              >
                Chính sách bảo mật
              </button>
              của ứng dụng này.
            </p>
          </form>
        </div>
      </Col>
    </Row>
  );
};
